import type { Project } from "@secretaa/sdk";
import { qk, STALE_TIME } from "@secretaa/sdk-react";
import { Button, Card, EmptyState, Field } from "@secretaa/ui";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router";
import { api } from "../../app/api.ts";
import { useCurrentWorkspace } from "../../app/session.ts";
import { errorMessage } from "../../shared/errors.ts";
import { Form, FormError, PageHeader } from "../../shared/form.tsx";
import { useBlueprint } from "../project/project-hooks.ts";
import { RoleNeeded } from "../project/role-hint.tsx";

function useOrgUnits() {
  const cur = useCurrentWorkspace()!;
  return useQuery({ queryKey: qk.orgUnits(cur.tenant), queryFn: () => api.orgUnits.list(cur.workspaceId), staleTime: STALE_TIME.workspaceMeta });
}

export function ProjectsListPage() {
  const { t, i18n } = useTranslation();
  const cur = useCurrentWorkspace()!;
  const [status, setStatus] = useState<"active" | "archived">("active");
  const units = useOrgUnits();
  const list = useInfiniteQuery({
    queryKey: qk.projects(cur.tenant, { status }),
    queryFn: ({ pageParam }) => api.projects.list(cur.workspaceId, { status, ...(pageParam ? { cursor: pageParam } : {}) }),
    initialPageParam: "",
    getNextPageParam: (last) => last.meta.page.nextCursor ?? undefined,
    staleTime: STALE_TIME.projectList,
  });
  const unitName = (id: string | null) => (id ? (units.data?.find((u) => u.id === id)?.name ?? "—") : t("projects.workspaceLevel"));
  const fmt = new Intl.DateTimeFormat(i18n.language, { dateStyle: "medium" });
  const rows = list.data?.pages.flatMap((p) => p.data) ?? [];
  return (
    <>
      <PageHeader
        title={t("projects.title")}
        actions={
          <Button asChild>
            <Link to={`/w/${cur.workspaceId}/projects/new`}>{t("projects.new")}</Link>
          </Button>
        }
      />
      <div role="tablist" className="mb-4 flex gap-2">
        {(["active", "archived"] as const).map((s) => (
          <Button key={s} role="tab" aria-selected={status === s} variant={status === s ? "primary" : "secondary"} onClick={() => setStatus(s)}>
            {t(`projects.filter.${s}`)}
          </Button>
        ))}
      </div>
      {list.isError ? <FormError message={errorMessage(t, list.error)} /> : null}
      {list.isPending ? (
        <p>{t("app.loading")}</p>
      ) : rows.length === 0 ? (
        <EmptyState title={t("projects.empty")} />
      ) : (
        <Card className="overflow-x-auto p-0">
          <table className="w-full text-sm">
            <thead className="border-b border-(--saa-border) text-left text-(--saa-fg-muted)">
              <tr>
                <th className="px-4 py-2 font-medium">{t("projects.columns.name")}</th>
                <th className="px-4 py-2 font-medium">{t("projects.columns.key")}</th>
                <th className="px-4 py-2 font-medium">{t("projects.columns.orgUnit")}</th>
                <th className="px-4 py-2 font-medium">{t("projects.columns.status")}</th>
                <th className="px-4 py-2 font-medium">{t("projects.columns.createdAt")}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((p) => (
                <tr key={p.id} className="border-b border-(--saa-border) last:border-0">
                  <td className="px-4 py-2">
                    <Link className="font-medium underline-offset-2 hover:underline" to={`/w/${cur.workspaceId}/projects/${p.id}`}>
                      {p.name}
                    </Link>
                  </td>
                  <td className="px-4 py-2 font-mono">{p.key}</td>
                  <td className="px-4 py-2">{unitName(p.orgUnitId)}</td>
                  <td className="px-4 py-2">{t(`projects.status.${p.status}`)}</td>
                  <td className="px-4 py-2">{fmt.format(new Date(p.createdAt))}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
      {list.hasNextPage ? (
        <Button className="mt-4" variant="secondary" disabled={list.isFetchingNextPage} onClick={() => void list.fetchNextPage()}>
          {t("common.loadMore")}
        </Button>
      ) : null}
    </>
  );
}

function suggestKey(name: string): string {
  const ascii = name.normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/đ/gi, "D").toUpperCase();
  const letters = ascii.replace(/[^A-Z0-9 ]/g, "").split(/\s+/).filter(Boolean);
  const k = (letters.length > 1 ? letters.map((w) => w[0]).join("") : (letters[0] ?? "")).replace(/^[0-9]+/, "").slice(0, 10);
  return k.length >= 2 ? k : "";
}

export function ProjectNewPage() {
  const { t } = useTranslation();
  const cur = useCurrentWorkspace()!;
  const navigate = useNavigate();
  const qc = useQueryClient();
  const units = useOrgUnits();
  const [name, setName] = useState("");
  const [key, setKey] = useState<string | null>(null);
  const [unit, setUnit] = useState("");
  const keyValue = key ?? suggestKey(name);
  const create = useMutation({
    mutationFn: () => api.projects.create(cur.workspaceId, { name: name.trim(), key: keyValue, orgUnitId: unit || null }),
    onSuccess: async (p) => {
      qc.setQueryData(qk.project(cur.tenant, p.id), { data: p });
      await qc.invalidateQueries({ queryKey: qk.projects(cur.tenant).slice(0, 3) });
      await qc.invalidateQueries({ queryKey: qk.recentActivity(cur.tenant) });
      navigate(`/w/${cur.workspaceId}/projects/${p.id}`);
    },
  });
  return (
    <>
      <PageHeader title={t("projects.new")} />
      <Card className="max-w-lg">
        <Form label={t("projects.new")} onSubmit={() => create.mutate()}>
          <FormError message={create.isError ? errorMessage(t, create.error) : null} />
          <Field label={t("projects.fields.name")} required value={name} onChange={(e) => setName(e.target.value)} />
          <Field label={t("projects.fields.key")} required hint={t("projects.fields.keyHint")} value={keyValue} onChange={(e) => setKey(e.target.value.toUpperCase())} />
          <label className="flex flex-col gap-1.5 text-sm font-medium">
            {t("projects.fields.orgUnit")}
            <select className="h-9 rounded-md border border-(--saa-border) bg-(--saa-surface) px-2 font-normal" value={unit} onChange={(e) => setUnit(e.target.value)}>
              <option value="">{t("projects.workspaceLevel")}</option>
              {units.data
                ?.filter((u) => !u.archived)
                .map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name}
                  </option>
                ))}
            </select>
          </label>
          <Button type="submit" disabled={create.isPending || !name.trim() || !keyValue}>
            {t("common.create")}
          </Button>
        </Form>
      </Card>
    </>
  );
}

export function ProjectDetailPage() {
  const { t, i18n } = useTranslation();
  const cur = useCurrentWorkspace()!;
  const { projectId = "" } = useParams();
  const qc = useQueryClient();
  const members = useQuery({ queryKey: qk.members(cur.tenant), queryFn: () => api.members.list(cur.workspaceId), staleTime: STALE_TIME.workspaceMeta });
  const project = useQuery({ queryKey: qk.project(cur.tenant, projectId), queryFn: async () => ({ data: await api.projects.get(cur.workspaceId, projectId) }), staleTime: STALE_TIME.projectDefinition });
  const [name, setName] = useState<string | null>(null);
  const onSaved = async (p: Project) => {
    qc.setQueryData(qk.project(cur.tenant, p.id), { data: p });
    await qc.invalidateQueries({ queryKey: qk.projects(cur.tenant).slice(0, 3) });
  };
  const rename = useMutation({ mutationFn: () => api.projects.update(cur.workspaceId, projectId, { name: (name ?? "").trim() }, project.data!.data.version), onSuccess: async (p) => (await onSaved(p), setName(null)) });
  const archive = useMutation({ mutationFn: () => api.projects.archive(cur.workspaceId, projectId, project.data!.data.version), onSuccess: onSaved });
  const restore = useMutation({ mutationFn: () => api.projects.restore(cur.workspaceId, projectId, project.data!.data.version), onSuccess: onSaved });
  if (project.isPending) return <p>{t("app.loading")}</p>;
  if (project.isError) return <FormError message={errorMessage(t, project.error)} />;
  const p = project.data.data;
  const fmt = new Intl.DateTimeFormat(i18n.language, { dateStyle: "medium", timeStyle: "short" });
  return (
    <>
      <PageHeader
        title={p.name}
        actions={
          <div className="flex flex-col items-end gap-1">
            {p.status === "active" ? (
              <Button variant="secondary" disabled={archive.isPending} onClick={() => window.confirm(t("projects.detail.archiveConfirm")) && archive.mutate()}>
                {t("common.archive")}
              </Button>
            ) : (
              <div className="flex items-center gap-2">
                <span className="text-sm">{t("projects.status.archived")}</span>
                <Button variant="secondary" disabled={restore.isPending} onClick={() => window.confirm(t("projects.detail.restoreConfirm")) && restore.mutate()}>
                  {t("projects.detail.restore")}
                </Button>
              </div>
            )}
            <RoleNeeded projectId={p.id} needs="manager" />
          </div>
        }
      />
      <FormError message={rename.isError ? errorMessage(t, rename.error) : archive.isError ? errorMessage(t, archive.error) : restore.isError ? errorMessage(t, restore.error) : null} />
      <div className="grid gap-6 lg:grid-cols-3">
        <Card className="lg:col-span-1">
          <dl className="grid grid-cols-2 gap-y-2 text-sm">
            <dt className="text-(--saa-fg-muted)">{t("projects.columns.key")}</dt>
            <dd className="font-mono">{p.key}</dd>
            <dt className="text-(--saa-fg-muted)">{t("projects.columns.status")}</dt>
            <dd>{t(`projects.status.${p.status}`)}</dd>
            <dt className="text-(--saa-fg-muted)">{t("projects.detail.createdBy")}</dt>
            <dd>{members.data?.find((m) => m.userId === p.createdBy)?.displayName ?? "—"}</dd>
            <dt className="text-(--saa-fg-muted)">{t("projects.detail.createdAt")}</dt>
            <dd>{fmt.format(new Date(p.createdAt))}</dd>
            <dt className="text-(--saa-fg-muted)">{t("projects.detail.version")}</dt>
            <dd>{p.version}</dd>
          </dl>
          {p.status === "active" ? (
            <Form label={t("projects.detail.rename")} onSubmit={() => rename.mutate()}>
              <div className="mt-4">
                <Field label={t("projects.fields.name")} value={name ?? p.name} onChange={(e) => setName(e.target.value)} />
              </div>
              <Button type="submit" variant="secondary" disabled={rename.isPending || !(name ?? "").trim() || name === p.name}>
                {t("projects.detail.rename")}
              </Button>
            </Form>
          ) : null}
        </Card>
        <div className="lg:col-span-2">
          <ProjectDefinition projectId={p.id} />
        </div>
      </div>
    </>
  );
}

/** Tổng quan: có bản thiết kế ⇒ tóm tắt + mở Dữ liệu; chưa có ⇒ empty state dẫn sang Build (mọi vai trò thấy nút + gợi ý vai trò). */
function ProjectDefinition({ projectId }: { projectId: string }) {
  const { t } = useTranslation();
  const cur = useCurrentWorkspace()!;
  const bp = useBlueprint(projectId);
  const base = `/w/${cur.workspaceId}/projects/${projectId}`;
  if (bp.isPending) return <p>{t("app.loading")}</p>;
  if (bp.isError) return <FormError message={errorMessage(t, bp.error)} />;
  if (!bp.data.manifest) {
    return (
      <EmptyState
        title={t("projects.detail.noDefinition")}
        description={t("projects.detail.noDefinitionHint")}
        action={
          <div className="flex flex-col items-center gap-1">
            <Button asChild>
              <Link to={`${base}/build`}>{t("projects.detail.openBuild")}</Link>
            </Button>
            <RoleNeeded projectId={projectId} needs="manager" />
          </div>
        }
      />
    );
  }
  return (
    <Card className="flex flex-col gap-3">
      <p className="text-sm">{t("projects.detail.definition", { version: bp.data.activeVersion, count: bp.data.manifest.collections.filter((c) => c.status === "active").length })}</p>
      <div>
        <Button asChild>
          <Link to={`${base}/data`}>{t("projects.detail.openData")}</Link>
        </Button>
      </div>
    </Card>
  );
}
